import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {getFaq, type FaqResponse} from "@corporatesclub/shared";
import {DsButton} from "@corporatesclub/ui";
import {paths} from "@/lib/paths";
import {useI18n} from "./I18nProvider";
import {InfoPageHero} from "./InfoPageHero";

const TAB_KEYS = ["General", "Orders", "Payments", "Delivery", "Returns", "Support"] as const;

export function FaqView() {
  const {t} = useI18n();
  const [faq, setFaq] = useState<FaqResponse | null>(null);
  const [tab, setTab] = useState(1);
  const [open, setOpen] = useState<Set<number>>(() => new Set([0]));
  useEffect(() => {
    getFaq().then((data) => {
      setFaq(data);
      if(data.categories[0]) setTab(data.categories[0].id);
    });
  }, []);
  useEffect(() => {
    setOpen(new Set([0]));
  }, [tab]);
  if(!faq) {
    return (
      <div className="cc-infoPage cc-faq cc-faq--loading" aria-busy="true">
        <div className="cc-infoPage__hero--sk" />
        <div className="cc-infoPage__container">
          <div className="cc-faq__loading">
            {Array.from({length: 5}).map((_, i) => <div key={i} className="cc-faq__sk" />)}
          </div>
        </div>
      </div>
    );
  }
  const active = faq.categories.find((c) => c.id === tab) || faq.categories[0];
  function toggleItem(index: number) {
    setOpen((prev) => {
      const next = new Set(prev);
      if(next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }
  return (
    <div className="cc-infoPage cc-faq">
      <InfoPageHero title={t("FAQ")} />
      <div className="cc-infoPage__container">
        <p className="cc-faq__subtitle">{t("FAQ page subtitle")}</p>

        <ul className="cc-faq__tabs" role="tablist">
          {faq.categories.map((c, i) => (
            <li key={c.id} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={tab === c.id}
                className={`cc-faq__tab anim${tab === c.id ? " cc-faq__tab--active" : ""}`}
                onClick={() => setTab(c.id)}
              >
                {t(TAB_KEYS[i] || "General")}
              </button>
            </li>
          ))}
        </ul>

        {active ? (
          <ul className="cc-faq__list" role="tabpanel">
            {active.items.map((item, i) => {
              const isOpen = open.has(i);
              return (
                <li key={i} className={`cc-faq__item${isOpen ? " cc-faq__item--open" : ""}`}>
                  <button
                    type="button"
                    className="cc-faq__question anim"
                    aria-expanded={isOpen}
                    onClick={() => toggleItem(i)}
                  >
                    <span className="cc-faq__questionText">{item.question}</span>
                    <span className="cc-faq__icon" aria-hidden="true">
                      <span className="material-symbols-rounded">expand_more</span>
                    </span>
                  </button>
                  <div className="cc-faq__answer" dangerouslySetInnerHTML={{__html: item.answer}} />
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="cc-faq__cta">
          <div className="cc-faq__ctaText">
            <p className="cc-faq__ctaTitle">{t("Still have questions?")}</p>
            <p className="cc-faq__ctaDesc">{t("FAQ contact hint")}</p>
          </div>
          <Link to={paths.contact} className="cc-faq__ctaBtn anim">
            <DsButton variant="primary" size="md">{t("Contact us")}</DsButton>
          </Link>
        </div>
      </div>
    </div>
  );
}
